import { SET_SCALE, FIT_TO_WIDTH_OFF } from './types';
import { DEFAULT_SCALES, PDF_CONTENT_MARGIN } from '../../client/constants';
import getPdfViewerApplication from '../../client/utils/pdfjs/pdfViewerApplication';

/**
 * The preferred display scale factor is determined by the browser window width.
 */
export function calculateDisplayScale() {
  const clientWidth = window.innerWidth - PDF_CONTENT_MARGIN;
  return clientWidth > 1800 ? DEFAULT_SCALES.LARGE_SCREEN : DEFAULT_SCALES.SMALL_SCREEN;
}

export function to4Decimal(val) {
  return parseFloat(val.toFixed(4));
}

// Chrome zoom scales in percent
const zoomLevels = [25, 33, 50, 67, 75, 80, 90, 100, 110, 125, 150, 175, 200, 250, 300, 400, 500];
// Calculate the actual scales relative to the value of the preferred display scale
const displayScale = calculateDisplayScale();
const scales = zoomLevels.map(zoom => ({
  zoomLevel: zoom,
  scale: to4Decimal((zoom * displayScale) / 100),
}));

/**
 * Find the max available scale
 */
function applyScalingCap(nextIndex, maxScale) {
  for (let i = nextIndex - 1; i > -1; i -= 1) {
    const { scale } = scales[i];
    if (scale <= maxScale) {
      return scale;
    }
  }

  return maxScale;
}

export function getNextScale(direction = 1) {
  const pdfScale = getPdfViewerApplication().getPdfScale();
  let currentIndex = scales.findIndex(({ scale }) => scale === pdfScale);

  // if the scale was not found in the array
  if (currentIndex < 0) {
    // find closest index
    const [closestScale] = [...scales].sort(
      (prev, next) => (Math.abs(prev.scale - pdfScale) > Math.abs(next.scale - pdfScale) ? 1 : -1),
    );

    currentIndex = scales.findIndex(({ scale }) => scale === closestScale.scale);
  }

  const nextIndex = currentIndex + direction;
  if (nextIndex < 0 || nextIndex >= scales.length) {
    return pdfScale;
  }

  const nextScale = scales[nextIndex].scale;
  const maxScale = getPdfViewerApplication().getMaxScale();

  return (nextScale <= maxScale) ? nextScale : applyScalingCap(nextIndex, maxScale);
}

export function setScale(scale) {
  return (dispatch) => {
    // avoid invalid scale given by scale calculator function e.g. getFitToWidthPdfScale
    if (scale > 0) {
      getPdfViewerApplication().setPdfScale(scale);
      dispatch({ type: SET_SCALE, payload: scale });
    }
  };
}

export function zoomIn() {
  return (dispatch, getState) => {
    const { stayFitToWidth } = getState();
    if (stayFitToWidth) {
      dispatch({ type: FIT_TO_WIDTH_OFF });
    }
    dispatch(setScale(getNextScale(1)));
  };
}

export function zoomOut() {
  return (dispatch, getState) => {
    const { stayFitToWidth } = getState();
    if (stayFitToWidth) {
      dispatch({ type: FIT_TO_WIDTH_OFF });
    }
    dispatch(setScale(getNextScale(-1)));
  };
}

function calculateFitToWidthScale(pageWidth) {
  const availableWidth = window.innerWidth - PDF_CONTENT_MARGIN;
  const fitToWidthScale = availableWidth / pageWidth;

  return to4Decimal(fitToWidthScale);
}

/**
 * By default the PDF will be rendered either at the preferred display scale or fit-to-width
 * (the smallest scale factor takes precedence).
 */
function setDefaultScale(pageWidth) {
  const fitToWidthScale = calculateFitToWidthScale(pageWidth);
  const defaultScale = Math.min(displayScale, fitToWidthScale);

  return setScale(defaultScale);
}

/**
 * The PDF document will not have been registered with the viewer when the scale as first set.
 * As such the document width must be obtained directly from the viewport of the PDF page.
 */
export function setInitialScale(pageWidth) {
  return setDefaultScale(pageWidth);
}

export function resetScale() {
  const pageWidth = getPdfViewerApplication().getPageWidth(0);
  return setDefaultScale(pageWidth);
}
